import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  TPaymentHistory,
  TTradeDetailsById,
  TTradeDocument,
} from "../types/trades/trades";
import moment from "moment";

export type TAddArrivalDetails = {
  qty: number;
  arlDate: string;
  arlTime: string;
  destVehNum: string;
  freightAmt: number;
  freightAdv: number;
  freightPaid: number;
  srcWeight: number;
  destWeight: number;
  moistureLoss: number;
  arlComments: string;
  destWastage: number;
  netWeight: number;
  rate: number;
  destRate: number;
  cmdityCost: number;
  misc: number;
  invAmt: number;
  weightUnit: string;
  rateType: string | number;
  driverName: string;
  driverMobile: string | number;
  rateUnit: string;
  totalQty: number;
  totalWeight: number;
  totalNetWeight: number;
};

export type TAddArrivalDocuments = {
  arrival: TTradeDocument[];
  arrivalWight: TTradeDocument[];
  patti: TTradeDocument[];
};
type TInitialState = {
  paymentHistoryObj: TPaymentHistory | null;
  paymentUpdateStatus: boolean;
  isPaymentGateway: boolean;
  editAdvancePayment: boolean;
  tradeType: string;
  addArrivalDetails: TAddArrivalDetails;
  arrivalDocs: TAddArrivalDocuments;
  arrivalPmtToggle: boolean;
  arrivalpmtHistory: boolean;
  openTransportPmtEdit: boolean;
};

const initialState: TInitialState = {
  paymentHistoryObj: null,
  paymentUpdateStatus: false,
  isPaymentGateway: false,
  editAdvancePayment: false,
  tradeType: "",
  addArrivalDetails: {
    arlDate: "",
    arlTime: "",
    destVehNum: "",
    freightAmt: 0,
    freightAdv: 0,
    freightPaid: 0,
    destWeight: 0,
    moistureLoss: 0,
    destWastage: 0,
    netWeight: 0,
    rate: 0,
    destRate: 0,
    cmdityCost: 0,
    misc: 0,
    invAmt: 0,
    srcWeight: 0,
    arlComments: "",
    qty: 0,
    weightUnit: "",
    rateType: "",
    driverName: "",
    driverMobile: "",
    rateUnit: "",
    totalQty: 0,
    totalWeight: 0,
    totalNetWeight: 0,
  },
  arrivalDocs: {
    arrival: [],
    arrivalWight: [],
    patti: [],
  },
  arrivalPmtToggle: false,
  arrivalpmtHistory: false,
  openTransportPmtEdit: false,
};

export const arrivalDetails = createSlice({
  name: "arrivalDetails",
  initialState: initialState,
  reducers: {
    resetArrivalDetails: (state) => {
      return {
        ...initialState,
      };
    },
    getPaymentHistory: (
      state,
      action: PayloadAction<{
        paymentHistoryObj: TPaymentHistory | null;
      }>
    ) => {
      state.paymentHistoryObj = action.payload.paymentHistoryObj;
    },

    updatePaymentUpdateStatus: (
      state,
      action: PayloadAction<{
        paymentUpdateStatus: boolean;
        isPaymentGateway?: boolean;
      }>
    ) => {
      state.paymentUpdateStatus = action.payload.paymentUpdateStatus;
      state.isPaymentGateway = action.payload.isPaymentGateway ?? false;
    },
    updateEditAdvPayment: (
      state,
      action: PayloadAction<{
        editAdvancePayment: boolean;
      }>
    ) => {
      state.editAdvancePayment = action.payload.editAdvancePayment;
    },

    updateTradeType: (state, action: PayloadAction<string>) => {
      state.tradeType = action.payload;
    },

    setAddArrival: (
      state,
      action: PayloadAction<{
        tradeDetail: TTradeDetailsById;
      }>
    ) => {
      let tradeDetail = action.payload.tradeDetail;
      state.addArrivalDetails.arlDate = tradeDetail?.arlDate
        ? tradeDetail?.arlDate
        : moment(new Date()).format("YYYY-MM-DD");
      state.addArrivalDetails.arlTime = tradeDetail?.arlTime;
      state.addArrivalDetails.destVehNum = tradeDetail?.freightDtls?.destVehNum;
      state.addArrivalDetails.freightAmt = Number(tradeDetail?.freightAmt);
      state.addArrivalDetails.freightAdv = Number(tradeDetail?.freightAdv);
      state.addArrivalDetails.freightPaid = Number(tradeDetail?.freightPaid);
      // state.addArrivalDetails.srcWeight = Number(
      //   tradeDetail?.lineItems[0].weight
      // );
      // state.addArrivalDetails.destWeight = Number(
      //   tradeDetail?.lineItems[0].destWeight
      // );
      // state.addArrivalDetails.moistureLoss = tradeDetail?.moistureLoss;
      // state.addArrivalDetails.destWastage = Number(
      //   tradeDetail?.lineItems[0]?.addWastage
      // );
      // state.addArrivalDetails.netWeight =
      //   Number(tradeDetail?.lineItems[0]?.destWeight) -
      //   Number(tradeDetail?.lineItems[0]?.addWastage);
      state.addArrivalDetails.rate = Number(tradeDetail?.freightDtls?.rate);
      // state.addArrivalDetails.destRate = Number(
      //   tradeDetail?.lineItems[0]?.landingRate
      // );
      state.addArrivalDetails.cmdityCost = Number(tradeDetail?.cmdityCost);
      state.addArrivalDetails.misc = Number(tradeDetail?.misc);
      state.addArrivalDetails.invAmt = Number(tradeDetail?.invAmt);
      state.addArrivalDetails.arlComments =
        tradeDetail?.progress?.find(
          (p) =>
            p.progState === "PATTI_PENDING" || p.progState === "PMT_PENDING"
        )?.comments || "";
      // state.addArrivalDetails.qty = Number(tradeDetail?.lineItems[0]?.qty);
      // state.addArrivalDetails.weightUnit =
      //   tradeDetail?.lineItems[0]?.weightUnit;
      state.arrivalDocs = {
        ...state.arrivalDocs,
        arrival: [],
        arrivalWight: [],
      };
      state.addArrivalDetails.rateType = tradeDetail?.rateType;
      state.addArrivalDetails.rateUnit = tradeDetail?.freightDtls?.rateUnit;
      state.addArrivalDetails.driverName =
        tradeDetail?.freightDtls?.destDriverName;
      state.addArrivalDetails.driverMobile =
        tradeDetail?.freightDtls?.destDriverMobile ?? "";
      state.addArrivalDetails.totalNetWeight = tradeDetail?.totalNetWeight;
      state.addArrivalDetails.totalWeight = tradeDetail?.totalDestWeight;
      state.addArrivalDetails.totalQty = tradeDetail?.totalDestQty;
    },

    udpateAddArrival: (
      state,
      action: PayloadAction<{
        arrival: TAddArrivalDetails;
      }>
    ) => {
      state.addArrivalDetails = action.payload.arrival;
      state.arrivalDocs = {
        ...state.arrivalDocs,
        arrival: [],
        arrivalWight: [],
        patti: [],
      };
    },

    setArrivalDocuments: (state, action: PayloadAction<TTradeDocument[]>) => {
      state.arrivalDocs.arrival = action.payload;
    },

    setArrivalWeightDocuments: (
      state,
      action: PayloadAction<TTradeDocument[]>
    ) => {
      state.arrivalDocs.arrivalWight = action.payload;
    },

    setPattiDocuments: (state, action: PayloadAction<TTradeDocument[]>) => {
      state.arrivalDocs.patti = action.payload;
    },

    clearPattiDocuments: (state) => {
      state.arrivalDocs.patti = [];
    },
    clearArrivalDocs: (state) => {
      state.arrivalDocs.arrival = [];
    },
    setArrivalPmtToggle: (state, action: PayloadAction<boolean>) => {
      state.arrivalPmtToggle = action.payload;
    },
    setArrivalPmtHistory: (state, action: PayloadAction<boolean>) => {
      state.arrivalpmtHistory = action.payload;
    },
    setOpenArrivalPmtEdit: (state, action: PayloadAction<boolean>) => {
      state.openTransportPmtEdit = action.payload;
    },
  },
});
export const {
  getPaymentHistory,
  updateEditAdvPayment,
  updatePaymentUpdateStatus,
  updateTradeType,
  setAddArrival,
  udpateAddArrival,
  setArrivalDocuments,
  setArrivalWeightDocuments,
  setPattiDocuments,
  clearPattiDocuments,
  setArrivalPmtToggle,
  setArrivalPmtHistory,
  clearArrivalDocs,
  setOpenArrivalPmtEdit,
} = arrivalDetails.actions;
export default arrivalDetails.reducer;
